<template>
  <div>
    <form
      id="payment-form"
      ref="paymentform"
    >
      <v-row
        class="card-base-background border-radius-small pa-4"
        :class="{ 'shadow-card white': !isUpdate }"
        no-gutters
      >
        <v-col cols="12">
          <div class="mb-2">
            <v-text-field
              id="chn"
              v-model="defaultNameOnCard"
              class="custom-field-payment-style field-custom-label field-background-white"
              placeholder="Name on card"
              height="46"
              dense
              outlined
              :rules="[rules.required, rules.cardholder]"
            >
              <template #prepend>
                <label class="text text-m-small-bold">Cardholder name</label>
              </template>
            </v-text-field>
          </div>
        </v-col>
        <v-col cols="12">
          <div class="mb-2">
            <v-text-field
              id="card-holder-address"
              v-model="defaultAddress"
              class="custom-field-payment-style field-custom-label field-background-white"
              placeholder="Address"
              height="46"
              dense
              outlined
              :rules="[rules.required, rules.paymentCardField(defaultAddress, 'billing address')]"
            >
              <template #prepend>
                <label class="text text-m-small-bold">Billing address</label>
              </template>
            </v-text-field>
          </div>
        </v-col>
        <v-col cols="12">
          <div class="mb-2">
            <v-text-field
              id="card-holder-city"
              v-model="defaultCity"
              class="custom-field-payment-style field-custom-label field-background-white"
              placeholder="City"
              height="46"
              dense
              outlined
              :rules="[rules.required, rules.paymentCardField(defaultCity, 'city')]"
            >
              <template #prepend>
                <label class="text text-m-small-bold">City</label>
              </template>
            </v-text-field>
          </div>
        </v-col>
        <v-col cols="7">
          <div class="mb-2 pr-2">
            <v-text-field
              id="card-holder-state"
              v-model="defaultState"
              class="custom-field-payment-style field-custom-label field-background-white"
              placeholder="State"
              height="46"
              dense
              outlined
              :rules="[rules.required, rules.paymentCardField(defaultState, 'state')]"
            >
              <template #prepend>
                <label class="text text-m-small-bold">State</label>
              </template>
            </v-text-field>
          </div>
        </v-col>
        <v-col cols="5">
          <div class="mb-2">
            <v-text-field
              id="card-holder-postalcode"
              v-model="defaultPostalCode"
              type="number"
              class="custom-field-payment-style field-custom-label field-background-white"
              placeholder="ZIP"
              height="46"
              dense
              outlined
              :rules="[rules.required, rules.zip]"
            >
              <template #prepend>
                <label class="text text-m-small-bold">ZIP</label>
              </template>
            </v-text-field>
          </div>
        </v-col>
        <v-col cols="12">
          <div id="payment-element">
            <!-- Mount the Payment Element here -->
          </div>
        </v-col>
      </v-row>
      <v-row
        v-if="!isUpdate"
        class="justify-end mt-5"
      >
        <v-col class="col-12 col-sm-auto">
          <!-- Button for payments when user do not have payment method -->
          <v-btn
            id="first-time-payment-button"
            block
            color="primary white--text"
            ripple
            depressed
            :height="$vuetify.breakpoint.xsOnly ? '44px' : '36px'"
            class="heading heading-s-small px-sm-6"
            :loading="loadingSubmit"
            :disabled="loadingSubmit || fieldsUncomplete || $hasToDisablePayments"
          >
            Confirm payment
          </v-btn>
        </v-col>
      </v-row>
    </form>
  </div>
</template>

<script>
import BillingCardDetailsComponent from '@/components/billing/BillingCardDetailsComponent';
import ValidatorComponent from '@/components/common/ValidatorComponent';
import { loadStripe } from '@stripe/stripe-js/pure';
import { mapActions, mapGetters } from 'vuex';
export default {
  components: { BillingCardDetailsComponent },
  props: ['itemSelected', 'isOnboarding', 'isUpdate', 'promoCode'],
  data: () => ({
    elements: null,
    defaultAddress: null,
    defaultCity: null,
    defaultState: null,
    defaultPostalCode: null,
    defaultNameOnCard: null,
    defaultCardNumber: null,
    defaultCardDate: null,
    loadingSubmit: false,
    loadingForm: false,
    stripe: null,
    intentClientSecret: null,
    rules: ValidatorComponent.data().rules,
    setupIntentSucceeded: false,
    setupIntentResponse: null,
    stripeFieldsCompleted: false,
    userSubscription: null,
  }),
  computed: {
    defaultFullAddress() {
      return `${this.defaultAddress}. ${this.defaultCity}, ${this.defaultState} ${this.defaultPostalCode}`;
    },
    fieldsUncomplete() {
      //We need to grab those values from the Stripe iframe itself to check if they are empty or not in order to enable the button
      return !(
        this.defaultNameOnCard &&
        this.defaultAddress &&
        this.defaultCity &&
        this.defaultState &&
        this.defaultPostalCode &&
        this.stripeFieldsCompleted
      );
    },
  },
  async mounted() {
    this.loadingForm = true;
    this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY, {
      locale: 'en',
    });

    const appearance = {
      theme: 'none',
      variables: {
        colorPrimary: '#263684',
        colorText: '#0A0A0A',
        colorDanger: '#EB5953',
        colorDangerText: '#EB5953',
        colorSuccess: '#13B577',
        colorTextSecondary: '#757575',
        fontSizeBase: '16px',
        fontSizeSm: '12px',
        fontFamily: 'IBM Plex Sans',
        fontLineHeight: '24px',
        fontWeightNormal: '700',
        colorTextPlaceholder: '#757575',
        borderRadius: '4px',
        spacingGridRow: '28px',
        fontSize3Xs: '14px',
      },
      rules: {
        '.Input': {
          border: '1px solid #0A0A0A',
        },
        '.Input:focus': {
          border: 'none',
        },
      },
    };

    const fonts = [{ cssSrc: 'https://fonts.googleapis.com/css?family=IBM+Plex+Sans:400,700&display=swap' }];

    const options = {
      mode: 'payment',
      amount: parseInt(this.itemSelected.price),
      currency: 'usd',
      paymentMethodCreation: 'manual',
      appearance,
      fonts,
    };

    this.elements = this.stripe.elements(options);
    const paymentElement = this.elements.create('payment', {
      fields: {
        billingDetails: {
          name: 'auto',
          address: {
            country: 'never',
            city: 'never',
            state: 'never',
            line1: 'never',
            postalCode: 'never',
          },
        },
      },
    });

    paymentElement.mount('#payment-element');

    let self = this;
    paymentElement.addEventListener('change', function (event) {
      self.stripeFieldsCompleted = event ? event.complete : false;
    });

    paymentElement.on('ready', function () {
      self.$emit('loaded');
    });

    if (!this.isUpdate) {
      this.setupFirstTimePayment();
    }

    const response = await this.getUserSubscription();

    if (response.data) {
      this.userSubscription = response.data;
    }

    this.loadingForm = false;
  },
  methods: {
    ...mapActions('subscriptions', ['getPaymentIntent', 'postConfirmSingleCharge', 'getUserSubscription']),
    ...mapActions('profile', ['deletePaymentMethod', 'addPaymentMethod', 'checkIfPaymentMethodWillExpire']),
    showError(fieldError) {
      this.$toasted.global.showError({ message: fieldError });
    },
    validateCustomField(fieldValidationResult) {
      return fieldValidationResult === true;
    },
    confirmPayment(paymentMethod, item) {
      this.postConfirmSingleCharge({ paymentMethod, item, promoCode: this.promoCode })
        .then((response) => {
          this.$emit('payment-success', response);
          this.$toasted.global.showSuccess({ message: response.message });
          this.loadingSubmit = !this.loadingSubmit;
        })
        .catch((error) => {
          this.$toasted.global.showError({
            message: error.response.data ? error.response.data.message : 'Unexpected error',
          });
          this.loadingSubmit = !this.loadingSubmit;
          this.setupIntentSucceeded = false;
        });
    },
    setupFirstTimePayment() {
      const cardButton = document.querySelector('#first-time-payment-button');

      cardButton.addEventListener('click', async (e) => {
        e.preventDefault();

        const cardHolderAddress = document.querySelector('#card-holder-address');
        const cardHolderCity = document.querySelector('#card-holder-city');
        const cardHolderState = document.querySelector('#card-holder-state');

        const validateName = ValidatorComponent.data().rules.cardholder(this.defaultNameOnCard);
        const validateAddress = ValidatorComponent.data().rules.paymentCardField(
          this.defaultAddress,
          cardHolderAddress.placeholder,
        );
        const validateCity = ValidatorComponent.data().rules.paymentCardField(
          this.defaultCity,
          cardHolderCity.placeholder,
        );
        const validateState = ValidatorComponent.data().rules.paymentCardField(
          this.defaultState,
          cardHolderState.placeholder,
        );
        const validatePostalCode = ValidatorComponent.data().rules.zip(this.defaultPostalCode);

        if (!this.validateCustomField(validateName)) {
          this.showError(validateName);
        } else if (!this.validateCustomField(validateAddress)) {
          this.showError(validateAddress);
        } else if (!this.validateCustomField(validateCity)) {
          this.showError(validateCity);
        } else if (!this.validateCustomField(validateState)) {
          this.showError(validateState);
        } else if (!this.validateCustomField(validatePostalCode)) {
          this.showError(validatePostalCode);
        } else {
          this.loadingSubmit = !this.loadingSubmit;

          // this line is Stripe mandatory, otherwise you will get errors
          const { error: submitError } = await this.elements.submit();
          if (submitError) {
            console.log('error', submitError);
            return;
          }

          let elements = this.elements;
          let paymentMethod = null;

          paymentMethod = await this.stripe.createPaymentMethod({
            elements,
            params: {
              billing_details: {
                name: document.querySelector('#chn').value,
                address: {
                  country: 'US',
                  line1: document.querySelector('#card-holder-address').value,
                  city: document.querySelector('#card-holder-city').value,
                  state: document.querySelector('#card-holder-state').value,
                  postal_code: document.querySelector('#card-holder-postalcode').value,
                },
              },
            },
          });

          // payment happens here
          this.confirmPayment(paymentMethod.paymentMethod.id, this.itemSelected.priceHash);
        }
      });
    },
  },
};
</script>
<style scoped>
.full-height-payment-form-in-modal {
  height: auto;
  position: relative;
}
.button-at-bottom {
  position: absolute;
  bottom: 0;
  right: 0;
}
</style>
